/**
 * ChallengeProfileEdit
 *
 * Brandable profile-edit overlay for Challenge-context flows. Replaces the
 * platform's `EditProfilePictureModal` (the legacy modal-based) for any surface
 * that wants a brand-themed experience — RTTR is the first consumer, with
 * Polaris × CoD and future campaigns coming next.
 *
 * Design notes:
 *   - No the legacy modal. Uses ReactDOM.createPortal to mount at document.body
 *     so the fixed-position overlay escapes transformed/clipped ancestors in
 *     the host page (RTTR hub has multiple transformed sections that would
 *     otherwise pin a non-portaled fixed element to a parent's coordinate
 *     space and push the panel off-screen).
 *   - Brand tokens come in as a `brand` prop and are applied via inline
 *     CSS custom properties — no SCSS variable wiring needed per campaign.
 *   - All API state lives in existing platform hooks; this component is
 *     pure presentation + form orchestration.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import { USE_GET_USER_PROFILE_KEY, useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLeaderboardNickname } from "@/hooks/useGetUserLeaderboardNickname";
import { useUpdateLeaderboardNickname } from "@/hooks/useUpdateLeaderboardNickname";
import { useUpdateProfilePicture } from "@/hooks/useUpdateProfilePicture";
import { useDeleteAvatar } from "@/hooks/useDeleteAvatar";
import { addError } from "@/redux/meta/metaActions";
import { resolveUserAvatar } from "@/utils/resolveUserAvatar";
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./ChallengeProfileEdit.scss";

export interface ChallengeBrandTokens {
    /** Primary CTA + input focus border + edit-pencil overlay. Default platform purple. */
    accentColor?: string;
    /** Hover/active accent. */
    accentHoverColor?: string;
    /** Panel surface background. */
    surfaceBg?: string;
    /** Backdrop color. Default black at 60% opacity. */
    backdropBg?: string;
    /** Body text color on the panel surface. */
    surfaceFg?: string;
    /** Muted/sub text color. */
    surfaceFgMuted?: string;
    /** Input field background. */
    inputBg?: string;
    /** Font family (e.g. var(--f-ml) for Minelab Industry). */
    fontFamily?: string;
    /** Optional small logo (top-left of panel). */
    logoUrl?: string;
    /** Alt text for the logo. */
    logoAlt?: string;
}

interface ChallengeProfileEditProps {
    isOpen: boolean;
    onClose: () => void;
    brand?: ChallengeBrandTokens;
    /** Extra class applied to the root .cpe element — use to set CSS vars via SCSS rather than inline style. */
    brandClassName?: string;
    /** Heading text. Defaults to "Edit Profile". */
    title?: string;
    /** Sub-copy under the name input. */
    helpText?: string;
}

const NAME_MIN = 2;
const NAME_MAX = 32;
const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export const ChallengeProfileEdit: React.FC<ChallengeProfileEditProps> = ({
    isOpen,
    onClose,
    brand,
    brandClassName,
    title = "Edit Profile",
    helpText = "Use the name you'd like to appear on the leaderboard.",
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const { data: currentUser, refetch: refetchUserProfile } = useGetUserProfile(fullToken);
    const { data: nickData, refetch: refetchNickname } = useGetUserLeaderboardNickname(fullToken);

    const { mutateAsync: uploadProfilePicture } = useUpdateProfilePicture();
    const { mutateAsync: updateNickname } = useUpdateLeaderboardNickname();
    const { mutateAsync: deleteAvatar } = useDeleteAvatar();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cropperRef = useRef<ReactCropperElement>(null);
    const [nameInput, setNameInput] = useState("");
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
    const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    /*
     * Inline error message for nickname-specific failures (taken, length).
     * Rendered under the input so users can fix without a global toast.
     */
    const [nameError, setNameError] = useState<string | null>(null);

    // Seed name input from current nickname when the modal opens.
    useEffect(() => {
        if (isOpen && nickData) {
            setNameInput(nickData.nickname ?? "");
        }
    }, [isOpen, nickData]);

    /*
     * Object URLs are GC roots — without explicit revoke the browser holds
     * the underlying blob in memory until tab close. Revoke on unmount or
     * when the user picks a different file.
     */
    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) {
                URL.revokeObjectURL(pendingPreviewUrl);
            }
        };
    }, [pendingPreviewUrl]);

    /*
     * When the modal closes, fully reset pending-pick state. Without this,
     * `pendingFile` + `pendingPreviewUrl` survive the conditional-return
     * (React preserves state across `return null`), and the next open would
     * render the prior file's now-revoked object URL as the avatar → broken
     * image + stale "Remove" affordance reflecting state from a session the
     * user thought was discarded.
     */
    useEffect(() => {
        if (!isOpen) {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
            if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
            setPendingPreviewUrl(null);
            setPendingFile(null);
            setCropImageUrl(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        };
    }, [cropImageUrl]);

    if (!isOpen) {
        return null;
    }

    /*
     * skipGravatar: this drawer hosts RTTR + Polaris-CoD avatar edits.
     * Original behavior was profilePicture-only (no gravatar fallback) —
     * preserved to keep the "no upload yet" state as branded initials
     * rather than a stray gravatar identicon during the edit flow.
     */
    const resolvedAvatarUrl = resolveUserAvatar(currentUser?.user, "", { skipGravatar: true });
    const currentAvatarUrl = pendingPreviewUrl ?? (resolvedAvatarUrl || undefined);

    const initials = (nickData?.nickname ?? currentUser?.user?.nickname ?? "?")
        .slice(0, 2)
        .toUpperCase();

    const handleFilePick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        if (!ACCEPTED_TYPES.includes(file.type)) {
            dispatch(await addError({ message: "Image must be JPEG, PNG, WebP, or GIF." } as ApiError));
            return;
        }
        if (file.size > MAX_AVATAR_BYTES) {
            dispatch(await addError({ message: "Image must be under 5 MB." } as ApiError));
            return;
        }
        // Open crop overlay before committing the file
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(URL.createObjectURL(file));
        e.target.value = "";
    };

    const handleCropConfirm = async () => {
        if (!cropperRef.current?.cropper) return;
        setIsCropping(true);
        try {
            const blob: Blob = await new Promise((resolve, reject) => {
                const canvas = cropperRef.current?.cropper.getCroppedCanvas();
                if (!canvas) {
                    reject(new Error("Could not generate cropped image. Please try again."));
                    return;
                }
                canvas.toBlob(b => {
                    if (b) resolve(b); else reject(new Error("Could not encode cropped image."));
                });
            });
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
            const croppedUrl = URL.createObjectURL(blob);
            setPendingPreviewUrl(croppedUrl);
            setPendingFile(new File([blob], "avatar.jpg", { type: blob.type }));
            URL.revokeObjectURL(cropImageUrl);
            setCropImageUrl(null);
        } finally {
            setIsCropping(false);
        }
    };

    const handleCropCancel = () => {
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
    };

    const handleSave = async () => {
        setNameError(null);
        const trimmedName = nameInput.trim();
        /*
         * Empty nicknames are valid platform-wide (leaderboard renders
         * Player{N} as the display fallback). Only enforce the length range
         * when the user actually typed something — otherwise a user opening
         * the modal solely to change their avatar gets locked out.
         */
        if (trimmedName.length > 0 && (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX)) {
            setNameError(`Nickname must be between ${NAME_MIN} and ${NAME_MAX} characters.`);
            return;
        }

        setIsSaving(true);
        try {
            const tasks: Array<Promise<unknown>> = [];

            if (trimmedName !== (nickData?.nickname ?? "")) {
                tasks.push(updateNickname(trimmedName));
            }
            if (pendingFile) {
                tasks.push(uploadProfilePicture({
                    asset: pendingFile,
                    name: pendingFile.name,
                    description: "User avatar",
                }));
            }

            await Promise.all(tasks);

            await Promise.all([
                refetchUserProfile(),
                refetchNickname(),
            ]);
            // Invalidate so any other surfaces re-read the new state.
            await queryClient.invalidateQueries({ queryKey: [USE_GET_USER_PROFILE_KEY] });
            // Invalidate standings so leaderboard avatarUrl reflects the new profile picture.
            await queryClient.invalidateQueries({ queryKey: ["challenge-slug-standings"] });

            onClose();
        } catch (e) {
            /*
             * @devour/client errors are raw Response objects — body via
             * `.json()`. Clone first so a fallthrough to addError can still
             * parse the body. Backend wraps formErrors values as ApiError
             * instances ({type, message}); accept bare-string shape too.
             */
            let body: any = null;
            try {
                body = await (e as any)?.clone?.()?.json();
            } catch { /* not a Response, body consumed, or invalid JSON */ }
            const raw = body?.formErrors?.nickname;
            const code = typeof raw === "string" ? raw : raw?.message;
            if (code === "Taken") {
                setNameError("That nickname is taken. Please pick another.");
            } else if (code === "NotAllowed") {
                setNameError("That nickname is not allowed, choose another.");
            } else {
                dispatch(await addError(e as ApiError));
            }
            /*
             * Partial-failure recovery: if `updateNickname` resolved but
             * `uploadProfilePicture` rejected (or vice-versa), the server
             * already holds the new nickname while `nickData` is still
             * stale. Re-seeding the input from server state on next open
             * would otherwise show the pre-update value, making it look
             * like the rename was lost. Re-fetch on either path so the
             * input is always seeded from authoritative server state.
             */
            await refetchNickname();
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveAvatar = async () => {
        try {
            await deleteAvatar(currentUser?.user?.profilePicture?.id);
            // Clear local preview if any.
            if (pendingPreviewUrl) {
                URL.revokeObjectURL(pendingPreviewUrl);
                setPendingPreviewUrl(null);
            }
            setPendingFile(null);
            await refetchUserProfile();
        } catch (e) {
            dispatch(await addError(e as ApiError));
        }
    };

    /*
     * Brand tokens → inline CSS custom properties. The SCSS reads `var(...)`
     * with fallbacks so missing tokens degrade to platform defaults.
     */
    const styleVars: React.CSSProperties = {
        ...brand?.accentColor && { ["--cpe-accent" as string]: brand.accentColor },
        ...brand?.accentHoverColor && { ["--cpe-accent-hover" as string]: brand.accentHoverColor },
        ...brand?.surfaceBg && { ["--cpe-surface-bg" as string]: brand.surfaceBg },
        ...brand?.backdropBg && { ["--cpe-backdrop-bg" as string]: brand.backdropBg },
        ...brand?.surfaceFg && { ["--cpe-surface-fg" as string]: brand.surfaceFg },
        ...brand?.surfaceFgMuted && { ["--cpe-surface-fg-muted" as string]: brand.surfaceFgMuted },
        ...brand?.inputBg && { ["--cpe-input-bg" as string]: brand.inputBg },
        ...brand?.fontFamily && { ["--cpe-font" as string]: brand.fontFamily },
    };

    // Crop overlay — rendered on top of the panel when a file is picked
    const cropOverlay = cropImageUrl ? createPortal(
        <div className={["cpe cpe--crop", brandClassName].filter(Boolean).join(" ")} style={styleVars} role="dialog" aria-modal="true">
            <div className="cpe__backdrop" onClick={handleCropCancel} aria-hidden="true" />
            <div className="cpe__panel cpe__panel--crop" onClick={e => e.stopPropagation()}>
                <div className="cpe__header">
                    <h2 className="cpe__title">Position Your Photo</h2>
                    <button type="button" className="cpe__close" onClick={handleCropCancel} aria-label="Cancel">×</button>
                </div>
                <Cropper
                    ref={cropperRef}
                    className="cpe__cropper"
                    aspectRatio={1}
                    initialAspectRatio={1}
                    src={cropImageUrl}
                    viewMode={1}
                    minCropBoxHeight={80}
                    minCropBoxWidth={80}
                    background={false}
                    responsive={true}
                    autoCropArea={0.8}
                    checkOrientation={true}
                    guides={true}
                    dragMode="move"
                />
                <button
                    type="button"
                    className="cpe__save"
                    onClick={handleCropConfirm}
                    disabled={isCropping}
                >
                    {isCropping ? "Saving…" : "Confirm"}
                </button>
            </div>
        </div>,
        document.body,
    ) : null;

    const mainPanel = createPortal(
        <div
            className={["cpe", brandClassName].filter(Boolean).join(" ")}
            style={styleVars}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cpe-title"
        >
            <div
                className="cpe__backdrop"
                onClick={onClose}
                aria-hidden="true"
            />

            <div className="cpe__panel" onClick={(e) => e.stopPropagation()}>
                <div className="cpe__header">
                    {brand?.logoUrl &&
                        <img
                            className="cpe__logo"
                            src={brand.logoUrl}
                            alt={brand.logoAlt ?? ""}
                        />
                    }
                    <h2 id="cpe-title" className="cpe__title">{title}</h2>
                    <button
                        type="button"
                        className="cpe__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="cpe__avatar-wrap">
                    <div className="cpe__avatar-ring">
                        <button
                            type="button"
                            className="cpe__avatar"
                            onClick={handleFilePick}
                            aria-label="Change profile picture"
                        >
                            {currentAvatarUrl
                                ? <img src={currentAvatarUrl} alt="" className="cpe__avatar-img" />
                                : <span className="cpe__avatar-initials">{initials}</span>
                            }
                        </button>
                        <span className="cpe__avatar-pencil" aria-hidden="true" onClick={handleFilePick}>
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                            </svg>
                        </span>
                    </div>
                    {currentAvatarUrl && !pendingFile &&
                        <button
                            type="button"
                            className="cpe__avatar-remove"
                            onClick={handleRemoveAvatar}
                        >
                            Remove
                        </button>
                    }
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_TYPES.join(",")}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                </div>

                <label className="cpe__field">
                    <span className="cpe__label">Nickname</span>
                    <input
                        type="text"
                        className="cpe__input"
                        value={nameInput}
                        onChange={(e) => {
                            setNameInput(e.target.value); setNameError(null);
                        }}
                        maxLength={NAME_MAX}
                        placeholder="Your nickname"
                        style={{ borderColor: "var(--cpe-accent, #ff261c)", outline: "none", boxShadow: "none" } as React.CSSProperties}
                    />
                </label>
                {nameError &&
                    <p className="cpe__field-error" style={{ color: "var(--cpe-accent, #ff261c)", marginTop: 4, fontSize: 13 }}>{nameError}</p>
                }
                <p className="cpe__help">{helpText}</p>

                <button
                    type="button"
                    className="cpe__save"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>,
        document.body,
    );

    return <>{mainPanel}{cropOverlay}</>;
};
